export interface Article {
  id: string;
  title: { en: string; zh: string };
  date: string;
  content: { en: string; zh: string };
}

export const articles: Article[] = [
  {
    id: 'reverse-engineering-llm',
    title: {
      en: 'RE-LM: Reverse Engineering Large Model',
      zh: 'RE-LM：逆向工程大模型',
    },
    date: '2026-03-03',
    content: {
      en: `# RE-LM: Reverse Engineering Large Model

## What is RE-LM?

RE-LM is an advanced, AI-driven reverse engineering assistant purposefully designed for binary analysis, vulnerability research, and malware forensics. Serving as our flagship product, it is the fundamental engine empowering every analysis capability we offer.

Traditional reverse engineering is notoriously time-consuming and labor-intensive. A senior analyst may dedicate eight to twelve hours tracing a single function within stripped, highly optimized machine code to derive actionable intelligence. RE-LM revolutionizes this paradigm. Trained on an extensive corpus of binary datasets—encompassing disassembly, decompiler outputs, annotated firmware, threat intelligence reports, and CVE analyses—the model comprehends low-level execution semantics with human-like expertise but at machine-level speeds.

Unlike general-purpose large language models with superficial assembly knowledge, RE-LM is an architecture-aware, domain-specific intelligence. Its entire pre-training and reinforcement-learning methodology was strictly optimized to overcome the complex hurdles of reverse engineering: ambiguous type recovery, control-flow obfuscation, calling-convention inference, and cross-architecture translation.

---

## Technical Architecture

RE-LM operates on a 72-billion-parameter Mixture-of-Experts (MoE) transformer backbone. During inference, only 18 billion parameters activate per token, ensuring low-latency execution while retaining the model's comprehensive knowledge base. Hosted on a distributed GPU cluster with dynamic load balancing, RE-LM delivers sub-second response times for the vast majority of queries.

### Training Data Pipeline

Our proprietary training corpus, refined over 24 months, comprises:

- **Public Open-Source Binaries.** Thousands of C, C++, and Rust repositories compiled across all optimization tiers (O0–O3, Os, Oz) and architectures (x86-64, ARM64, MIPS, RISC-V), mapping resulting disassembly directly to original source code.
- **Annotated Malware Samples.** Hundreds of thousands of labeled specimens from VirusTotal, MalwareBazaar, and premium threat-intelligence feeds, enriched with expert analyses detailing execution flows, evasion maneuvers, and C2 protocols.
- **CVE Reproduction Datasets.** Detailed historical data on tens of thousands of CVEs, incorporating vulnerable binaries, patched versions, diffs, and corresponding security advisories, allowing the model to correlate specific binary anti-patterns with known vulnerability classes.
- **Protocol Captures.** Millions of PCAP files spanning well-known and proprietary protocols, precisely aligned with Wireshark dissectors and manual protocol specifications.

---

## Core Capabilities

### 1. Decompilation Enhancement

By providing RE-LM with output from industry-standard decompilers (e.g., IDA Pro, Ghidra, Binary Ninja, Hex-Rays), users receive a highly sanitized, contextually annotated version of the code:

- **Symbol Recovery.** Predicts context-aware function and variable names, alongside precise type annotations.
- **Structure Reconstruction.** Accurately reconstructs complex struct layouts, vtables, COM interfaces, and linked-list definitions, transcending severe optimization and symbol stripping.
- **Control-Flow Clarification.** Demystifies obfuscated dispatchers, flattened control flows, and opaque predicates into intuitive if/else and switch branch logic.
- **Inline Documentation.** Automatically generates comprehensive natural-language summaries for every function, delineating purpose, inputs, outputs, and side effects.

### 2. Vulnerability Discovery

RE-LM's vulnerability engine autonomously queries decompiled code against an active repository of over 200 vulnerability patterns, including:

- Stack and heap buffer overflows
- Use-after-free (UAF) and double-free conditions
- Integer overflows and signedness errors
- Format-string vulnerabilities
- Race conditions (TOCTOU)
- Cryptographic misuse (e.g., hardcoded keys, weak PRNGs, ECB mode, unauthenticated encryption)
- Logical flaws within authentication and authorization routines

Upon detecting a potential flaw, the model instantly produces a structured report featuring the vulnerable code path, a root-cause analysis, an exploitability assessment (CVSS vector), and actionable remediation guidance.

### 3. Protocol Reverse Engineering

Upload a PCAP file or raw byte stream, and RE-LM will:

- Identify message boundaries and complex session structures.
- Propose precise field-level definitions (offsets, lengths, basic types, endianness).
- Detect encryption, compression, or encoding layers, and recommend effective decryption strategies.
- Generate a Wireshark-compatible Lua dissector or Scapy protocol layer ready for immediate integration.

### 4. Malware Triage and Forensics

For incident responders and malware analysts operating under strict time constraints, RE-LM delivers:

- **Family Classification.** Attributes samples to recognized malware families with a 96.4% Top-3 accuracy on our internal benchmarks.
- **Configuration Extraction.** Automatically isolates and decrypts embedded C2 URLs, RSA/AES keys, mutexes, and persistence registry paths.
- **Anti-Analysis Inventory.** Systematically catalogues every anti-debugging, anti-VM, and anti-sandbox technique employed by the sample, offering recommended bypass strategies.
- **Behavioral Narrative.** Provides a cohesive, plain-language execution summary delineating the malware's step-by-step actions, ideal for immediate inclusion in incident reports.

---

## Enterprise Cost Reduction & Efficiency Gains

**In today's cybersecurity landscape, specialized talent is exceptionally scarce and highly compensated. RE-LM dramatically alters the economics of reverse engineering by augmenting human analysts, leading to unprecedented cost reductions and efficiency gains.**

- **Accelerated Turnaround Times.** Tasks that traditionally require 8 to 12 hours of manual tracing can now be completed in minutes. By automating the most tedious aspects of reverse engineering (e.g., symbol recovery and de-obfuscation), analyzing a critical malware sample or finding a zero-day vulnerability shifts from a multi-day effort to a single-session task.
- **Optimized Talent Utilization.** Senior reverse engineers and vulnerability researchers are freed from repetitive code sanitization. They can focus purely on high-value strategic analysis, threat hunting, and exploit development, effectively multiplying the output of your most expensive personnel without increasing headcount.
- **Rapid Incident Response.** In a live breach scenario, time is money. RE-LM enables Security Operations Centers (SOCs) and Incident Response (IR) teams to triage unknown binaries and extract actionable IOCs almost instantaneously, minimizing dwell time, reducing enterprise risk, and preventing catastrophic data loss.
- **Lower Training & Onboarding Costs.** RE-LM's natural language summaries and explanatory outputs act as a continuous, on-the-job mentor for junior analysts. By lowering the barrier to entry for understanding complex low-level code, organizations can rapidly upskill their existing workforce, reducing the reliance on ultra-niche, hard-to-find experts.

By integrating RE-LM into your security pipeline, enterprises typically observe a **4x to 6x increase in analysis throughput**, ensuring that your security operations remain scalable, robust, and economically sustainable.

---

## Pricing

RE-LM is available through two subscription tiers. Pricing is based on message and token usage limits within a rolling 5-hour time window.

| Plan | Messages / 5h | Tokens / 5h | Monthly | Quarterly | Yearly |
|---|---|---|---|---|---|
| Max | 100 | 6M | $60 | $165 | $600 |
| Ultra | 200 | 10M | $132 | $375 | $1350 |

### Max — from $60 / month

For penetration-testing consultants, red teams, and security startups. Provides 100 messages and 6M tokens per 5-hour window.

### Ultra — from $132 / month

For large security organizations, MSSPs, and government agencies. 200 messages and 10M tokens per 5-hour window with the highest throughput. Ultra includes API tool calling and MCP tools.

Quarterly and yearly plans are available for teams that prefer longer billing cycles, with yearly plans providing the best value.

---

## Integration

RE-LM exposes a RESTful JSON API and ships with official plugins for IDA Pro (7.x / 8.x / 9.x), Ghidra (10.x+), Binary Ninja (stable / dev), and a standalone CLI tool. Python and Go SDKs are published on PyPI and the Go module proxy respectively. Detailed integration guides, runnable examples, and an interactive API playground are available in the Documentation section of this dashboard.

---

## Security and Privacy

All binary data uploaded to RE-LM is encrypted in transit (TLS 1.3) and at rest (AES-256). Customer data is never used to train or fine-tune the model unless the customer explicitly opts in. Enterprise customers may deploy RE-LM on their own infrastructure with an air-gapped license for environments that prohibit external data transfer.
`,
      zh: `# RE-LM：逆向工程大模型

## 什么是 RE-LM？

RE-LM 是一款高级的、由 AI 驱动的逆向工程助手，专为二进制分析、漏洞研究和恶意软件取证而设计。作为我们的旗舰产品，它是赋予我们所有分析功能的核心底层引擎。

传统的逆向工程是一项公认的耗时且劳动密集型工作。一名资深分析师可能需要花费 8 到 12 个小时，在去符号化、高度优化的机器码中追踪单个函数，才能提取出具有可操作性的情报。RE-LM 彻底颠覆了这一模式。模型基于海量的二进制数据集（涵盖反汇编代码、反编译器输出、带注释的固件、威胁情报报告以及 CVE 分析）进行训练，使其能够像人类专家一样理解底层代码的执行语义，同时具备机器级别的处理速度。

与仅具备肤浅汇编知识的通用大语言模型不同，RE-LM 是一个具备架构感知能力的领域专用智能体。其整个预训练和强化学习方法论都经过了严格优化，旨在克服逆向工程中的核心技术壁垒：模糊类型恢复、控制流混淆、调用约定推断以及跨架构翻译。

---

## 技术架构

RE-LM 基于 720 亿参数的混合专家（MoE）Transformer 架构运行。在推理过程中，每个 token 仅激活 180 亿个参数，这确保了模型在保持其全面知识库的同时实现低延迟执行。RE-LM 托管在具有动态负载均衡功能的分布式 GPU 集群上，可为绝大多数查询提供亚秒级的响应时间。

### 训练数据管线

我们历时 24 个月精心构建的专有训练语料库包含：

- **公开开源二进制文件。** 数千个 C、C++ 和 Rust 代码仓库，在所有优化层级（O0–O3、Os、Oz）和架构（x86-64、ARM64、MIPS、RISC-V）下进行编译，并将生成的反汇编代码直接映射回原始源代码。
- **带注释的恶意软件样本。** 数十万个来自 VirusTotal、MalwareBazaar 和高级威胁情报源的标记样本，并辅以专家分析，详细说明执行流、逃逸策略和 C2 协议。
- **CVE 复现数据集。** 数万个 CVE 的详尽历史数据，整合了存在漏洞的二进制文件、已修复版本、代码差异和相应的安全通告，使模型能够将特定的二进制反模式与已知的漏洞类别相关联。
- **协议抓包数据。** 数以百万计的 PCAP 文件，涵盖已知和专有协议，与 Wireshark 解析器和人工协议规范精确对齐。

---

## 核心能力

### 1. 反编译增强

通过结合行业标准反编译器（如 IDA Pro、Ghidra、Binary Ninja、Hex-Rays）的输出，RE-LM 能够为用户提供经过高度清理、带有上下文注释的代码版本：

- **符号恢复。** 预测具有上下文感知能力的函数和变量名称，以及精确的类型注释。
- **结构重建。** 准确重建复杂的结构体布局、虚函数表 (vtable)、COM 接口和链表定义，突破深度优化和符号剥离的限制。
- **控制流澄清。** 将模糊的调度器、扁平化的控制流和不透明谓词解构为直观的 if/else 和 switch 分支逻辑。
- **内联文档。** 自动为每个函数生成全面的自然语言摘要，详细说明其用途、输入、输出和副作用。

### 2. 漏洞发现

RE-LM 的漏洞引擎能够针对一个包含 200 多种漏洞模式的活跃库，自主扫描和查询反编译代码，这些模式包括：

- 栈和堆缓冲区溢出
- 释放后重用 (UAF) 和双重释放 (Double-free)
- 整数溢出和符号错误
- 格式化字符串漏洞
- 竞态条件 (TOCTOU)
- 密码学误用（例如，硬编码密钥、弱 PRNG、ECB 模式、未认证加密）
- 认证和授权路径中的逻辑缺陷

在检测到潜在缺陷后，模型会立即生成一份结构化报告，其中包含漏洞代码路径、根本原因分析、可利用性评估（CVSS 向量）和可操作的修复指南。

### 3. 协议逆向工程

上传 PCAP 文件或原始字节流，RE-LM 将：

- 识别消息边界和复杂的会话结构。
- 提出精确的字段级定义（偏移量、长度、基本类型、字节序）。
- 检测加密、压缩或编码层，并推荐有效的解密策略。
- 生成兼容 Wireshark 的 Lua 解析器或 Scapy 协议层，可供立即集成。

### 4. 恶意软件分诊与取证

对于在严格时间限制下运行的事件响应人员和恶意软件分析师，RE-LM 能够提供：

- **家族分类。** 在我们的内部基准测试中，以 96.4% 的 Top-3 准确率将样本归入已知的恶意软件家族。
- **配置提取。** 自动定位并解密嵌入的 C2 地址、RSA/AES 密钥、互斥体名称和持久化注册表路径。
- **反分析清单。** 系统性地对样本采用的每种反调试、反虚拟机和反沙箱技术进行盘点分类，并提供推荐的绕过策略。
- **行为叙述。** 提供连贯的、通俗易懂的执行摘要，详细说明恶意软件的逐步操作，非常适合直接纳入事件总结和响应报告。

---

## 企业级降本增效

**在当今的网络安全环境中，专业人才是极其稀缺且薪酬高昂的。RE-LM 通过大幅度增强人类分析师的工作效能，极大地改变了逆向工程的经济学模型，为企业带来了前所未有的成本降低和效率提升。**

- **极速缩短交付周期。** 传统上需要 8 到 12 小时人工追踪的任务，现在只需几分钟即可完成。通过自动化逆向工程中最繁琐的环节（如符号恢复和去混淆），分析关键恶意软件样本或寻找零日漏洞的过程，从耗时数天的苦差事，直接跃升为单次会话级的高效任务。
- **优化核心人才利用率。** 资深逆向工程师和漏洞研究人员将从重复性的代码梳理中彻底解放出来。他们可以纯粹专注于高价值的战略分析、威胁狩猎和漏洞利用开发，在不增加任何员工人数的情况下，有效地将您最昂贵的人员产出成倍放大。
- **快速敏捷的事件响应。** 在真实的数据泄露和应急响应场景中，时间就是金钱。RE-LM 使安全运营中心 (SOC) 和事件响应 (IR) 团队能够几乎瞬间完成对未知二进制文件的分诊并提取可操作的 IOC (妥协指标)，从而最大限度地减少威胁停留时间，降低企业风险，并防止灾难性的数据丢失。
- **降低培训与结构性成本。** RE-LM 提供的自然语言摘要和解释性输出可作为初级分析师“全天候的在岗导师”。通过降低理解复杂底层代码的门槛，企业可以快速提升现有员工的技能水平，从而真正减少对极少数、难以招聘的顶尖专家的依赖。

通过将 RE-LM 集成到您的安全工作流中，企业通常可以观察到 **4 倍至 6 倍的分析吞吐量增长**，确保您的安全运营体系保持可扩展性、极高的稳健性，并在经济层面实现可观的可持续发展。

---

## 定价

RE-LM 提供两种订阅方案。定价基于消息使用限额——您在滚动时间窗口内可进行的分析查询次数。

| 方案 | 使用限额 | 窗口 | 月付 | 季付 | 年付 |
|---|---|---|---|---|---|
| Max | 100 条消息 | 5 小时 | $60 | $165 | $600 |
| Ultra | 200 条消息 | 5 小时 | $132 | $375 | $1350 |

### Max — $60 / 月起

面向渗透测试顾问、红队和安全创业公司。每 5 小时可发送 100 条消息，包含 600 万 token 窗口。

### Ultra — $132 / 月起

面向大型安全组织、托管安全服务提供商（MSSP）和政府机构。每 5 小时可发送 200 条消息，包含 1000 万 token 窗口，吞吐量最高。Ultra 支持 API tools 调用和 MCP tools。

季付和年付适合需要更长计费周期的团队，年付方案优惠最大。

---

## 集成

RE-LM 提供 RESTful JSON API，并附带 IDA Pro（7.x / 8.x / 9.x）、Ghidra（10.x+）、Binary Ninja（stable / dev）的官方插件以及独立 CLI 工具。Python 和 Go SDK 分别发布在 PyPI 和 Go 模块代理上。详细的集成指南、可运行示例和交互式 API 调试台可在本仪表盘的"文档"部分获取。

---

## 安全与隐私

上传至 RE-LM 的所有二进制数据在传输过程中使用 TLS 1.3 加密，在存储时使用 AES-256 加密。除非客户明确选择加入，否则客户数据永远不会被用于训练或微调模型。企业客户可通过物理隔离许可证将 RE-LM 部署到自有基础设施上，以满足禁止外部数据传输的合规要求。
`,
    },
  },
  {
    id: 'dynamic-analysis-sandbox',
    title: {
      en: 'Dynamic Analysis Sandbox (Under Development)',
      zh: '动态分析沙箱（研发中）',
    },
    date: '2026-03-03',
    content: {
      en: `# Dynamic Analysis Sandbox

> **Status: Under Development — Please Stay Tuned**

The Dynamic Analysis Sandbox is the second product in our ecosystem. It is a secure, hypervisor-based execution environment that will allow analysts to safely detonate suspicious binaries and scripts while collecting comprehensive behavioral telemetry.

## Why We Are Building It

Static analysis alone cannot reveal the full story. Packed, polymorphic, or heavily obfuscated malware often only shows its true behavior at runtime. Our sandbox will bridge this gap by providing a high-fidelity, anti-evasion execution environment that feeds results directly into RE-LM for AI-powered correlation and reporting.

## Planned Capabilities

- **Hardware-accelerated isolation.** Full QEMU/KVM-backed virtual machines with per-analysis snapshots, ensuring zero cross-contamination between samples.
- **Multi-platform targets.** Windows 7 through Windows 11, Ubuntu 20.04+, macOS 12+, and Android 11+ execution targets with pre-configured application stacks.
- **Deep telemetry capture.** Kernel-level hooks will record file system operations, registry modifications, network connections, DNS queries, process trees, API call sequences, and memory allocation patterns.
- **Anti-evasion technology.** Patched CPUID responses, realistic user artifacts (browser history, documents, printers), simulated mouse and keyboard input, and variable execution timing to defeat sandbox-detection heuristics.
- **Seamless RE-LM integration.** Behavioral reports and captured memory dumps will be automatically ingested by RE-LM to produce unified static + dynamic analysis summaries.

## Timeline

| Milestone | Target |
|---|---|
| Internal Alpha | Q2 2026 |
| Closed Beta | Q3 2026 |
| General Availability | Q1 2027 |

We will share more details as development progresses. If you would like early access, please contact our product team through the dashboard.
`,
      zh: `# 动态分析沙箱

> **状态：研发中 — 敬请期待**

动态分析沙箱是我们产品生态系统中的第二款产品。它是一个安全的、基于虚拟机管理程序的执行环境，将允许分析师安全地引爆可疑的二进制文件和脚本，同时收集全面的行为遥测数据。

## 为什么我们要打造它

仅靠静态分析无法揭示全貌。加壳、多态或重度混淆的恶意软件往往只有在运行时才会暴露真实行为。我们的沙箱将弥合这一差距，提供一个高保真、抗逃逸的执行环境，并将结果直接反馈给 RE-LM 进行 AI 驱动的关联分析和报告生成。

## 规划能力

- **硬件加速隔离。** 基于 QEMU/KVM 的完整虚拟机，支持每次分析独立快照，确保样本之间零交叉污染。
- **多平台目标。** 支持 Windows 7 至 Windows 11、Ubuntu 20.04+、macOS 12+ 以及 Android 11+ 的执行目标，并预配置应用程序栈。
- **深度遥测采集。** 内核级 Hook 将记录文件系统操作、注册表修改、网络连接、DNS 查询、进程树、API 调用序列和内存分配模式。
- **反逃逸技术。** 通过修补 CPUID 响应、设置真实用户痕迹（浏览器历史记录、文档、打印机）、模拟鼠标和键盘输入以及可变执行时间，来击败沙箱检测启发式算法。
- **与 RE-LM 无缝集成。** 行为报告和捕获的内存转储将被自动传入 RE-LM，生成统一的静态 + 动态分析摘要。

## 时间线

| 里程碑 | 目标 |
|---|---|
| 内部 Alpha | 2026 Q2 |
| 封闭 Beta | 2026 Q3 |
| 正式发布 | 2027 Q1 |

随着研发推进我们将分享更多细节。如果您希望获得早期访问权限，请通过仪表盘联系我们的产品团队。
`,
    },
  },
  {
    id: 'threat-intelligence-kb',
    title: {
      en: 'Threat Intelligence Knowledge Base (Under Development)',
      zh: '威胁情报知识库（研发中）',
    },
    date: '2026-03-03',
    content: {
      en: `# Threat Intelligence Knowledge Base

> **Status: Under Development — Please Stay Tuned**

The Threat Intelligence Knowledge Base (TI-KB) is the third product in our ecosystem. It will be a globally distributed, real-time graph database that maps relationships between threat actors, attack campaigns, malware families, indicators of compromise, and targeted industries — providing the contextual layer that connects RE-LM's technical findings to the broader threat landscape.

## Why We Are Building It

Technical analysis without context is incomplete. Knowing that a binary contains a specific backdoor is useful; knowing that the backdoor is associated with a particular APT group that has been targeting your industry sector for the past six months is actionable. TI-KB will close this gap automatically.

## Planned Capabilities

- **Real-time threat graph.** A continuously updated knowledge graph correlating threat actors, TTPs (mapped to MITRE ATT\&CK), malware families, infrastructure indicators, and victim industry verticals.
- **Automatic RE-LM enrichment.** When RE-LM encounters a suspicious hash, code signature, or network indicator during analysis, TI-KB will instantly provide attribution, campaign history, and related IOCs.
- **Custom alerting.** Subscribe to feeds filtered by industry, geography, or specific threat groups. Receive notifications when new intelligence matching your criteria is ingested.
- **Standards compliance.** Full STIX 2.1 and TAXII 2.1 support for integration with existing SIEM, SOAR, and SOC toolchains.
- **Community sharing.** Contribute and receive intelligence across organizational boundaries through our vetted sharing network, with built-in data anonymization controls.

## Timeline

| Milestone | Target |
|---|---|
| Design Partner Program | Q3 2026 |
| Closed Beta | Q4 2026 |
| General Availability | Q2 2027 |

We are designing this product in close collaboration with enterprise customers and industry partners. Contact our product team if you would like to participate in the design partner program.
`,
      zh: `# 威胁情报知识库

> **状态：研发中 — 敬请期待**

威胁情报知识库（TI-KB）是我们产品生态系统中的第三款产品。它将是一个全球分布、实时更新的图数据库，映射威胁行为者、攻击活动、恶意软件家族、妥协指标以及目标行业之间的关系 — 为 RE-LM 的技术发现与更广泛的威胁态势之间提供上下文关联层。

## 为什么我们要打造它

缺乏上下文的技术分析是不完整的。知道一个二进制文件中包含某个特定后门固然有用；但如果能进一步知道该后门与某个 APT 组织相关联，且该组织在过去六个月持续针对你所在的行业发起攻击 — 这才是真正可以付诸行动的情报。TI-KB 将自动弥合这一差距。

## 规划能力

- **实时威胁图谱。** 一个持续更新的知识图谱，关联威胁行为者、TTPs（映射至 MITRE ATT\&CK 框架）、恶意软件家族、基础设施指标和受害行业。
- **自动 RE-LM 增强。** 当 RE-LM 在分析过程中遇到可疑的哈希值、代码签名或网络指标时，TI-KB 将即时提供归因分析、活动历史和关联 IOC。
- **自定义告警。** 按行业、地理区域或特定威胁组织筛选并订阅情报源，在新情报命中您的监控条件时收到通知。
- **标准合规。** 完全支持 STIX 2.1 和 TAXII 2.1 标准，可与现有 SIEM、SOAR 和 SOC 工具链集成。
- **社区共享。** 通过我们审核过的共享网络在组织边界之间贡献和接收情报，内置数据匿名化控制功能。

## 时间线

| 里程碑 | 目标 |
|---|---|
| 设计合作伙伴计划 | 2026 Q3 |
| 封闭 Beta | 2026 Q4 |
| 正式发布 | 2027 Q2 |

我们正在与企业客户和行业合作伙伴密切协作进行产品设计。如果您有意参加设计合作伙伴计划，请联系我们的产品团队。
`,
    },
  },
];
